import { computed, ref, type ComputedRef, type Ref } from 'vue';

/**
 * Composable pour le pattern "filtrer + trier une liste en memoire" present
 * dans CreaturesView (biome + hostile), InventoryView (kind + rarity + search)
 * et 5+ autres vues. Centralise :
 *  - definition d'un dictionnaire de predicats activables a la volee
 *  - une recherche textuelle libre sur des champs choisis
 *  - un tri optionnel (asc/desc) avec callback de comparaison
 *
 * Volontairement simple : pas de pagination locale ici (les listes
 * KStone sont deja paginees cote API), pas de tri multi-colonnes
 * (Element Plus s'en occupe quand on utilise el-table avec sortable).
 *
 * Usage typique (InventoryView) :
 *
 *   const filterKind = ref<string | null>(null);
 *   const filterRarity = ref<string | null>(null);
 *   const search = ref('');
 *
 *   const filtered = useListFilter(items, {
 *     predicates: {
 *       kind: (i) => !filterKind.value || i.kind === filterKind.value,
 *       rarity: (i) => !filterRarity.value || i.rarity === filterRarity.value,
 *     },
 *     search: () => search.value,
 *     searchFields: (i) => [i.label, i.code],
 *     sort: { compare: (a, b) => a.rarity.localeCompare(b.rarity) },
 *   });
 *
 * Le composable expose le tableau filtre comme un computed reactif.
 */

export interface UseListFilterOptions<T> {
  /** Dictionnaire de predicats independants. Tous doivent retourner true pour
   *  qu'un item soit conserve. Une fonction qui retourne true sans condition
   *  agit comme "filtre desactive". */
  predicates?: Record<string, (item: T) => boolean>;
  /** Texte de recherche (deja en lowercase ou pas, on normalise). Lue a
   *  chaque recalcul, donc passer une fonction qui lit un ref reactif. */
  search?: () => string;
  /** Champs sur lesquels appliquer la recherche. Retourne un tableau de
   *  strings (ou null/undefined qu'on ignore) a comparer en lowercase. */
  searchFields?: (item: T) => Array<string | null | undefined>;
  /** Tri optionnel. compare suit la convention Array.sort (negatif si a<b). */
  sort?: {
    compare: (a: T, b: T) => number;
    /** Par defaut asc (compare tel quel). Si false, on inverse le signe. */
    asc?: boolean;
  };
}

export interface UseListFilterReturn<T> {
  /** Liste filtree + triee, recalculee reactivement. */
  filtered: ComputedRef<T[]>;
  /** Nombre d'items affiches (raccourci pour filtered.value.length). */
  count: ComputedRef<number>;
  /** Nombre d'items dans la source (avant filtrage). */
  total: ComputedRef<number>;
}

export function useListFilter<T>(
  source: Ref<T[]> | ComputedRef<T[]>,
  options: UseListFilterOptions<T> = {},
): UseListFilterReturn<T> {
  const { predicates, search, searchFields, sort } = options;

  const filtered = computed<T[]>(() => {
    const items = source.value ?? [];

    // 1. Filtrage par predicats : tous doivent passer.
    let result = predicates
      ? items.filter((it) => Object.values(predicates).every((p) => p(it)))
      : items;

    // 2. Recherche textuelle : on normalise tout en lowercase pour eviter
    //    la sensibilite a la casse. Si le terme est vide on skippe.
    if (search && searchFields) {
      const term = search().trim().toLowerCase();
      if (term.length > 0) {
        result = result.filter((it) => {
          const fields = searchFields(it);
          return fields.some(
            (f) => typeof f === 'string' && f.toLowerCase().includes(term),
          );
        });
      }
    }

    // 3. Tri : on clone avant pour ne pas muter la source.
    if (sort) {
      const sign = sort.asc === false ? -1 : 1;
      result = [...result].sort((a, b) => sign * sort.compare(a, b));
    }

    return result;
  });

  const count = computed(() => filtered.value.length);
  const total = computed(() => (source.value ?? []).length);

  return { filtered, count, total };
}

/**
 * Helper pour le pattern "filtre toggle on/off". Initialise un ref avec
 * la valeur off, expose un toggle et le predicat correspondant.
 *
 * const { value, toggle, predicate } = useToggleFilter('all', ['hostile','peaceful']);
 * useListFilter(items, { predicates: { hostility: predicate(i => i.isHostile ? 'hostile' : 'peaceful') } });
 */
export function useToggleFilter<TVal extends string>(
  initial: TVal,
  values: readonly TVal[],
) {
  const value = ref<TVal>(initial) as Ref<TVal>;
  function set(v: TVal): void { value.value = v; }
  return { value, set, values };
}
